import React, { useEffect, useState, useRef, Fragment, lazy, memo, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, ShieldCheckIcon, LockIcon, ZapIcon, ClockIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useCountdown } from '../hooks/useCountdown';
// ─── Checkout URL builder ─────────────────────────────────────────────────────
const buildUrl = (template: string) => {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? template.replace('{{fbclid}}', fbclid) : template;
};
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
let icFiredV6 = false;
const trackV6 = (name: string, price: number) => {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'AddToCart', {
    content_name: name,
    value: price,
    currency: 'USD'
  });
  if (!icFiredV6) {
    icFiredV6 = true;
    window.fbq('track', 'InitiateCheckout', {
      content_name: name,
      value: price,
      currency: 'USD'
    });
  }
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const ACCENT = '#FF6C00';
const ACCENT_GLOW = 'rgba(255,108,0,0.3)';
const BG = '#0B0B0B';
const GOLD = '#D4AF37';
// ─── Checkout URLs & Pricing ──────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_PREMIUM = 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BUNDLE = 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BASIC = 'https://legendaryreads.com/cart/42185022439506:1?checkout&fbclid={{fbclid}}';
const BASE_PRICE = 59.97;
const BUMP1_PRICE = 35;
const BUMP2_PRICE_STANDALONE = 220;
const BUMP2_PRICE_WITH_BUMP1 = 185;
type TierKey = 'DIGITAL' | 'PREMIUM' | 'BUNDLE';
interface TierInfo {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  url: string;
  desc: string;
  badge?: string;
}
const TIERS: Record<TierKey, TierInfo> = {
  DIGITAL: {
    id: 'digital',
    name: 'Complete Digital Collection',
    price: 59.97,
    originalPrice: 114.97,
    url: CHECKOUT_DIGITAL,
    desc: '4 Digital Books (Instant Access)'
  },
  PREMIUM: {
    id: 'premium',
    name: 'Premium Hardcover Bundle',
    price: 94.97,
    originalPrice: 127.97,
    url: CHECKOUT_PREMIUM,
    desc: 'Digital Collection + Premium Hardcover',
    badge: 'MOST POPULAR'
  },
  BUNDLE: {
    id: 'bundle',
    name: "Time Traveler's Collector Bundle",
    price: 279.97,
    originalPrice: 419.97,
    url: CHECKOUT_BUNDLE,
    desc: 'Digital + Hardcover + Paperbacks + Audiobook + Hat + Print + Tote + Mug'
  }
};
const TIER_KEYS: TierKey[] = ['DIGITAL', 'PREMIUM', 'BUNDLE'];
const SHOP_PAY_AMOUNTS: Record<TierKey, string> = {
  DIGITAL: '$15',
  PREMIUM: '$23.75',
  BUNDLE: '$70'
};
// ─── Images ───────────────────────────────────────────────────────────────────
const GALLERY_IMAGES = ['https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png', 'https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
const BOOK_IMAGES = ['https://cdn.magicpatterns.com/uploads/eRaLLFj1Xpd9yKh17SfpDw/BACK_IN_TIME_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/qWMGMZni8tSubnCQBBHeda/BEHIND_THE_CLOCK_TOWER_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/6N2oU2pwJLDZiEUA7vpeCu/GREAT_SCOTT_40_QUOTES_FOR_40_YEARS_COVER_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/8pvDLEzUAHUVNEPcPZcf74/TIME_IMAGINATION_DESTINY_-_COVER_DIGITAL.png'];
const BUMP1_ITEMS = ['Everything in the Complete Digital Collection', 'Back In Time — Premium Hardcover Edition', 'Delivery in 12 days'];
const BUMP2_ITEMS = ['Everything in the Complete Digital Collection', 'All 4 volumes in Paperback Edition', 'Back In Time Audiobook Experience', 'Hill Valley 1985 Limited Vintage Trucker Hat', "Flux Energy Core Collector's Framed Print — 10×10 Limited Edition", 'Time Machine Heritage Tote', '40 Years Travelling Through Time Commemorative Mug', 'Fast Delivery'];
const BUNDLE_IMAGES = ['https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
// ─── Shared primitives ────────────────────────────────────────────────────────
const OrangeButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  small?: boolean;
}) => <a href={href} onClick={onClick} className="cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg" style={{
  minHeight: small ? '44px' : '56px',
  padding: small ? '10px 16px' : '16px 24px',
  fontSize: small ? '14px' : '17px',
  background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
  color: '#0B0B0B',
  fontFamily: 'Inter, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}} data-id="element-1590">
    {children}
  </a>;
const BumpItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-2" data-id="element-1591">
    <span style={{
    color: ACCENT,
    flexShrink: 0,
    fontSize: '9px',
    marginTop: '5px'
  }} data-id="element-1592">
      ✦
    </span>
    <span className="text-xs leading-snug" style={{
    color: 'rgba(245,245,245,0.78)',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-1593">
      {text}
    </span>
  </li>;
const ImageLightbox = ({
  images,
  startIndex,
  onClose
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-1594">
      <div className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-1595">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close" data-id="element-1596">
          <XIcon className="h-7 w-7" data-id="element-1597" />
        </button>
        <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{
        aspectRatio: '1/1'
      }} data-id="element-1598">
          <img src={images[idx]} alt={`Item ${idx + 1}`} className="w-full h-full object-contain" decoding="async" loading="lazy" data-id="element-1599" />
        </div>
        <button onClick={() => setIdx(i => i === 0 ? images.length - 1 : i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Previous" data-id="element-1600">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-1601" />
        </button>
        <button onClick={() => setIdx(i => i === images.length - 1 ? 0 : i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Next" data-id="element-1602">
          <ChevronRightIcon className="h-5 w-5" data-id="element-1603" />
        </button>
        <div className="flex justify-center gap-1.5 mt-3" data-id="element-1604">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className="rounded-full transition-all duration-200" style={{
          width: i === idx ? '20px' : '8px',
          height: '8px',
          backgroundColor: i === idx ? ACCENT : 'rgba(255,255,255,0.3)'
        }} data-id="element-1605" />)}
        </div>
      </div>
    </div>;
};
// ─── A. STICKY ALERT BAR ─────────────────────────────────────────────────────
const AlertBar = () => <div className="w-full min-h-[32px] md:h-[36px] flex items-center justify-center px-3 text-center text-[7px] leading-tight md:text-sm font-bold tracking-wide sticky top-0 z-50 py-1 md:py-0 md:whitespace-nowrap overflow-hidden" style={{
  background: 'linear-gradient(90deg, #FF6C00 0%, #FFD300 100%)',
  color: '#0B0B0B'
}} data-id="element-1606">
    <span className="md:hidden" data-id="element-1607">
      40TH ANNIVERSARY OFFER — MAIN BOOK + 3 COMPANION BOOKS FREE
    </span>
    <span className="hidden md:inline" data-id="element-1608">
      40TH ANNIVERSARY LIMITED OFFER — BACK IN TIME MAIN BOOK + 3 COMPANION
      BOOKS FOR FREE
    </span>
  </div>;
// ─── HEADER ──────────────────────────────────────────────────────────────────
const HeaderV6 = () => <header className="bg-black text-white border-b border-orange-500/30 z-40 sticky top-[32px] md:top-[36px]" data-id="element-1609">
    <div className="container mx-auto px-4 py-2.5 flex justify-between items-center" data-id="element-1610">
      <span className="font-bold text-lg bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text font-future" data-id="element-1611">
        Back In Time
      </span>
      <a href="#purchase-panel" className="cta font-bold text-sm px-4 py-1.5 rounded-md transition-all hover:brightness-110" style={{
      background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
      color: '#0B0B0B',
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1612">
        Get My Copy
      </a>
    </div>
  </header>;
// ─── B. HERO (V5 layout + V4 dark styling) ───────────────────────────────────
const HeroV6 = () => {
  const [mainImage, setMainImage] = useState(GALLERY_IMAGES[0]);
  const [selectedTier, setSelectedTier] = useState<TierKey>('DIGITAL');
  const TIER_IMAGE: Record<TierKey, string> = {
    DIGITAL: GALLERY_IMAGES[0],
    PREMIUM: GALLERY_IMAGES[1],
    BUNDLE: GALLERY_IMAGES[2]
  };
  const handleTierChange = (key: TierKey) => {
    setSelectedTier(key);
    setMainImage(TIER_IMAGE[key]);
  };
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);
  const {
    secondsLeft
  } = useCountdown();
  const currentTier = TIERS[selectedTier];
  const checkoutUrl = buildUrl(currentTier.url);
  const savings = currentTier.originalPrice - currentTier.price;
  const savingsPercent = Math.round(savings / currentTier.originalPrice * 100);
  const hours = String(Math.floor(secondsLeft / 3600)).padStart(2, '0');
  const minutes = String(Math.floor(secondsLeft % 3600 / 60)).padStart(2, '0');
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const handleCta = () => {
    trackV6(currentTier.name, currentTier.price);
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          setTimeout(() => {
            const t = document.createElement('div');
            t.className = 'cta';
            t.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            t.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(t);
            t.dispatchEvent(new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            }));
            if (isMobile) {
              t.dispatchEvent(new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              }));
              t.dispatchEvent(new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              }));
            }
            setTimeout(() => {
              if (t.parentNode) t.parentNode.removeChild(t);
            }, 2000);
          }, isMobile ? 1000 : 500);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section className="py-6 md:py-10 relative overflow-hidden" ref={sectionRef} id="purchase-panel" style={{
    backgroundColor: BG
  }} data-id="element-1613">
      <div className="absolute inset-0 opacity-20" style={{
      background: 'radial-gradient(ellipse at 70% 50%, rgba(255,108,0,0.15) 0%, transparent 70%)'
    }} data-id="element-1614" />
      <div className="container mx-auto px-4 max-w-7xl relative z-10" data-id="element-1615">
        {/* ── MOBILE LAYOUT ── */}
        <div className="lg:hidden flex flex-col" data-id="element-1616">
          {/* 1. Headline */}
          <p className="text-xs font-bold uppercase tracking-widest mb-3 text-center" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.12em'
        }} data-id="element-1617">
            40th Anniversary Collector's Edition
          </p>
          <h1 className="text-[28px] md:text-5xl font-bold leading-tight mb-4 text-white font-future tracking-tight text-center" data-id="element-1618">
            You've Seen It 50 Times.{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1619">
              You've Never Read Anything Like This.
            </span>
          </h1>

          {/* 2. Text */}
          <p className="text-sm md:text-lg text-white/75 leading-relaxed mb-5 text-center max-w-xl mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1620">
            The only collector's edition that goes beyond the film — into the
            production truth, the cultural legacy, and the reason this story
            lives in your bones 40 years later. Available in Print or Digital.
            Yours to keep permanently.
          </p>

          {/* 3. Image */}
          <div className="mb-5" data-id="element-1621">
            <div className="mb-3 rounded-xl overflow-hidden shadow-2xl flex items-center justify-center" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(255,108,0,0.3)',
            aspectRatio: '1/1'
          }} data-id="element-1622">
              <img src={mainImage} alt="Product View" className="w-full h-full object-contain p-4" fetchpriority="high" width="600" height="600" data-id="element-1623" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-1624">
              {GALLERY_IMAGES.map((img, idx) => <button key={idx} onClick={() => setMainImage(img)} className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all" style={{
              border: mainImage === img ? `2px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)',
              opacity: mainImage === img ? 1 : 0.6
            }} data-id="element-1625">
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="80" height="80" data-id="element-1626" />
                </button>)}
            </div>
          </div>

          {/* 4. Price / Purchase */}
          <div className="flex items-center gap-2 mb-2 justify-center" data-id="element-1627">
            <div className="flex gap-0.5" data-id="element-1628">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1629" />)}
            </div>
            <span className="text-sm font-medium underline underline-offset-2" style={{
            color: 'rgba(245,245,245,0.7)'
          }} data-id="element-1630">
              5,354+ verified reviews
            </span>
          </div>

          <div className="flex flex-col md:grid md:grid-cols-2 gap-2 mb-5 text-sm" style={{
          color: 'rgba(245,245,245,0.8)'
        }} data-id="element-1631">
            <div className="flex items-center gap-2" data-id="element-1632">
              <CheckIcon className="h-4 w-4 flex-shrink-0" style={{
              color: ACCENT
            }} data-id="element-1633" />{' '}
              Digital + Print Available
            </div>
            <div className="flex items-center gap-2" data-id="element-1634">
              <CheckIcon className="h-4 w-4 flex-shrink-0" style={{
              color: ACCENT
            }} data-id="element-1635" />{' '}
              Direct from publisher
            </div>
            <div className="flex items-center gap-2" data-id="element-1636">
              <CheckIcon className="h-4 w-4 flex-shrink-0" style={{
              color: ACCENT
            }} data-id="element-1637" />{' '}
              Instant digital delivery
            </div>
          </div>

          <div className="mb-5" data-id="element-1638">
            <span className="inline-block text-[10px] font-bold px-2.5 py-1 rounded uppercase tracking-widest mb-2" style={{
            background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
            color: '#0B0B0B'
          }} data-id="element-1639">
              40th Anniversary Sale
            </span>
            <div className="flex items-baseline gap-3" data-id="element-1640">
              <span className="font-bold text-xl" style={{
              color: ACCENT
            }} data-id="element-1641">
                {savingsPercent}% OFF
              </span>
              <span className="font-bold text-2xl bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1642">
                ${currentTier.price.toFixed(2)}
              </span>
            </div>
            <div className="text-sm mt-1" style={{
            color: 'rgba(245,245,245,0.5)'
          }} data-id="element-1643">
              Regular price:{' '}
              <span className="line-through" data-id="element-1644">
                ${currentTier.originalPrice.toFixed(2)}
              </span>
            </div>
            <div className="mt-2" data-id="element-1645">
              <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full" style={{
              backgroundColor: 'rgba(255,108,0,0.15)',
              color: ACCENT,
              border: '1px solid rgba(255,108,0,0.3)'
            }} data-id="element-1646">
                You Save ${savings.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Tier Selector */}
          <div className="rounded-xl p-4 mb-5 shadow-lg" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,255,255,0.1)'
        }} data-id="element-1647">
            <h3 className="font-bold mb-3 text-sm tracking-wide" style={{
            color: '#F5F5F5',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1648">
              Select your edition:
            </h3>
            <div className="space-y-3" data-id="element-1649">
              {TIER_KEYS.map(key => {
              const tier = TIERS[key];
              const isSelected = selectedTier === key;
              const shopPayAmount = SHOP_PAY_AMOUNTS[key];
              return <label key={key} className="relative flex items-start p-3 rounded-lg cursor-pointer transition-all" style={{
                backgroundColor: isSelected ? 'rgba(255,108,0,0.08)' : 'rgba(255,255,255,0.03)',
                border: isSelected ? `1px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)'
              }} data-id="element-1650">
                    {tier.badge && <span className="absolute -top-2.5 right-3 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm" style={{
                  backgroundColor: GOLD,
                  color: '#0B0B0B'
                }} data-id="element-1651">
                        {tier.badge}
                      </span>}
                    <div className="flex items-center h-5 mt-0.5" data-id="element-1652">
                      <div className="w-4 h-4 rounded-full border flex items-center justify-center" style={{
                    borderColor: isSelected ? ACCENT : 'rgba(255,255,255,0.3)'
                  }} data-id="element-1653">
                        {isSelected && <div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: ACCENT
                    }} data-id="element-1654" />}
                      </div>
                      <input type="radio" name="tier-mobile" className="sr-only" checked={isSelected} onChange={() => handleTierChange(key)} data-id="element-1655" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-1656">
                      <div className="flex justify-between items-start" data-id="element-1657">
                        <span className="font-bold text-sm" style={{
                      color: '#F5F5F5'
                    }} data-id="element-1658">
                          {tier.name}
                        </span>
                        <span className="font-bold text-sm ml-2" style={{
                      color: '#F5F5F5'
                    }} data-id="element-1659">
                          ${tier.price.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs mt-1 leading-snug" style={{
                    color: 'rgba(245,245,245,0.6)'
                  }} data-id="element-1660">
                        {tier.desc}
                      </p>
                      <div className="mt-1.5" data-id="element-1661">
                        <span className="text-[10px] font-bold" style={{
                      color: ACCENT
                    }} data-id="element-1662">
                          You Save $
                          {(tier.originalPrice - tier.price).toFixed(2)}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{
                    color: 'rgba(245,245,245,0.5)'
                  }} data-id="element-1663">
                        <span data-id="element-1664">or 4 payments of</span>
                        <span className="font-bold" style={{
                      color: '#F5F5F5'
                    }} data-id="element-1665">
                          {shopPayAmount}
                        </span>
                        <span data-id="element-1666">with</span>
                        <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-3.5 inline-block brightness-110" width="48" height="14" loading="lazy" decoding="async" data-id="element-1667" />
                      </div>
                    </div>
                  </label>;
            })}
            </div>
          </div>

          <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-1668">
            GET MY COLLECTOR'S EDITION →
          </OrangeButton>

          <div className="flex items-center justify-center gap-3 mt-4" data-id="element-1669">
            <div className="flex items-center gap-1.5" data-id="element-1670">
              {[{
              v: hours,
              l: 'hrs'
            }, {
              v: minutes,
              l: 'min'
            }, {
              v: secs,
              l: 'sec'
            }].map(({
              v,
              l
            }) => <Fragment key={l} data-id="element-1671">
                  <div className="bg-[#1a1a1a] border border-white/10 text-white text-xs font-bold px-2 py-1 rounded min-w-[32px] text-center" data-id="element-1672">
                    {v}
                  </div>
                  <span className="text-[9px] text-white/50 uppercase font-bold tracking-wider" data-id="element-1673">
                    {l}
                  </span>
                </Fragment>)}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/60 font-medium" data-id="element-1674">
              <ClockIcon className="h-3.5 w-3.5" style={{
              color: ACCENT
            }} data-id="element-1675" />{' '}
              lock in savings now
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-5" data-id="element-1676">
            <div className="flex items-center gap-1.5" data-id="element-1677">
              <LockIcon className="h-4 w-4" style={{
              color: 'rgba(245,245,245,0.4)'
            }} data-id="element-1678" />
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{
              color: 'rgba(245,245,245,0.4)'
            }} data-id="element-1679">
                Secure Checkout
              </span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-1680">
              <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-1681" />
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{
              color: 'rgba(245,245,245,0.4)'
            }} data-id="element-1682">
                One-Time Payment
              </span>
            </div>
          </div>

          <button onClick={() => handleTierChange('BUNDLE')} className="mt-5 w-full py-2.5 rounded-lg text-sm font-bold tracking-wide transition-colors" style={{
          backgroundColor: 'transparent',
          border: '1px solid rgba(255,255,255,0.2)',
          color: '#F5F5F5'
        }} data-id="element-1683">
            ⊕ Bundle and save
          </button>
        </div>

        {/* ── DESKTOP LAYOUT ── */}
        <div className="hidden lg:flex lg:flex-row gap-10" data-id="element-1684">
          {/* LEFT: Image Gallery */}
          <div className="w-1/2" data-id="element-1685">
            <div className="mb-3 rounded-xl overflow-hidden shadow-2xl flex items-center justify-center" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(255,108,0,0.3)',
            aspectRatio: '1/1'
          }} data-id="element-1686">
              <img src={mainImage} alt="Product View" className="w-full h-full object-contain p-4" fetchpriority="high" width="600" height="600" data-id="element-1687" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-1688">
              {GALLERY_IMAGES.map((img, idx) => <button key={idx} onClick={() => setMainImage(img)} className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all" style={{
              border: mainImage === img ? `2px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)',
              opacity: mainImage === img ? 1 : 0.6
            }} data-id="element-1689">
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="80" height="80" data-id="element-1690" />
                </button>)}
            </div>
          </div>

          {/* RIGHT: Product Info */}
          <div className="w-1/2 flex flex-col" data-id="element-1691">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
            color: ACCENT,
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '0.12em'
          }} data-id="element-1692">
              40th Anniversary Collector's Edition
            </p>
            <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-4 text-white font-future tracking-tight" data-id="element-1693">
              You've Seen It 50 Times.{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1694">
                You've Never Read Anything Like This.
              </span>
            </h1>
            <p className="text-base mb-4 leading-relaxed" style={{
            color: 'rgba(245,245,245,0.75)',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1695">
              The only collector's edition that goes beyond the film — into the
              production truth, the cultural legacy, and the reason this story
              lives in your bones 40 years later. Available in Print or Digital.
              Yours to keep permanently.
            </p>
            <div className="flex items-center gap-2 mb-2" data-id="element-1696">
              <div className="flex gap-0.5" data-id="element-1697">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1698" />)}
              </div>
              <span className="text-sm font-medium underline underline-offset-2" style={{
              color: 'rgba(245,245,245,0.7)'
            }} data-id="element-1699">
                5,354+ verified reviews
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5 text-sm" style={{
            color: 'rgba(245,245,245,0.8)'
          }} data-id="element-1700">
              <div className="flex items-center gap-2" data-id="element-1701">
                <CheckIcon className="h-4 w-4 flex-shrink-0" style={{
                color: ACCENT
              }} data-id="element-1702" />{' '}
                Digital + Print Available
              </div>
              <div className="flex items-center gap-2" data-id="element-1703">
                <CheckIcon className="h-4 w-4 flex-shrink-0" style={{
                color: ACCENT
              }} data-id="element-1704" />{' '}
                Direct from publisher
              </div>
              <div className="flex items-center gap-2" data-id="element-1705">
                <CheckIcon className="h-4 w-4 flex-shrink-0" style={{
                color: ACCENT
              }} data-id="element-1706" />{' '}
                Instant digital delivery
              </div>
            </div>
            <div className="mb-5" data-id="element-1707">
              <span className="inline-block text-[10px] font-bold px-2.5 py-1 rounded uppercase tracking-widest mb-2" style={{
              background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
              color: '#0B0B0B'
            }} data-id="element-1708">
                40th Anniversary Sale
              </span>
              <div className="flex items-baseline gap-3" data-id="element-1709">
                <span className="font-bold text-xl md:text-2xl" style={{
                color: ACCENT
              }} data-id="element-1710">
                  {savingsPercent}% OFF
                </span>
                <span className="font-bold text-2xl md:text-3xl bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1711">
                  ${currentTier.price.toFixed(2)}
                </span>
              </div>
              <div className="text-sm mt-1" style={{
              color: 'rgba(245,245,245,0.5)'
            }} data-id="element-1712">
                Regular price:{' '}
                <span className="line-through" data-id="element-1713">
                  ${currentTier.originalPrice.toFixed(2)}
                </span>
              </div>
              <div className="mt-2" data-id="element-1714">
                <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                border: '1px solid rgba(255,108,0,0.3)'
              }} data-id="element-1715">
                  You Save ${savings.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Tier Selector */}
            <div className="rounded-xl p-4 mb-5 shadow-lg" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(255,255,255,0.1)'
          }} data-id="element-1716">
              <h3 className="font-bold mb-3 text-sm tracking-wide" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1717">
                Select your edition:
              </h3>
              <div className="space-y-3" data-id="element-1718">
                {TIER_KEYS.map(key => {
                const tier = TIERS[key];
                const isSelected = selectedTier === key;
                const shopPayAmount = SHOP_PAY_AMOUNTS[key];
                return <label key={key} className="relative flex items-start p-3 rounded-lg cursor-pointer transition-all" style={{
                  backgroundColor: isSelected ? 'rgba(255,108,0,0.08)' : 'rgba(255,255,255,0.03)',
                  border: isSelected ? `1px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)'
                }} data-id="element-1719">
                      {tier.badge && <span className="absolute -top-2.5 right-3 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm" style={{
                    backgroundColor: GOLD,
                    color: '#0B0B0B'
                  }} data-id="element-1720">
                          {tier.badge}
                        </span>}
                      <div className="flex items-center h-5 mt-0.5" data-id="element-1721">
                        <div className="w-4 h-4 rounded-full border flex items-center justify-center" style={{
                      borderColor: isSelected ? ACCENT : 'rgba(255,255,255,0.3)'
                    }} data-id="element-1722">
                          {isSelected && <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: ACCENT
                      }} data-id="element-1723" />}
                        </div>
                        <input type="radio" name="tier-desktop" className="sr-only" checked={isSelected} onChange={() => handleTierChange(key)} data-id="element-1724" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-1725">
                        <div className="flex justify-between items-start" data-id="element-1726">
                          <span className="font-bold text-sm" style={{
                        color: '#F5F5F5'
                      }} data-id="element-1727">
                            {tier.name}
                          </span>
                          <span className="font-bold text-sm ml-2" style={{
                        color: '#F5F5F5'
                      }} data-id="element-1728">
                            ${tier.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs mt-1 leading-snug" style={{
                      color: 'rgba(245,245,245,0.6)'
                    }} data-id="element-1729">
                          {tier.desc}
                        </p>
                        <div className="mt-1.5" data-id="element-1730">
                          <span className="text-[10px] font-bold" style={{
                        color: ACCENT
                      }} data-id="element-1731">
                            You Save $
                            {(tier.originalPrice - tier.price).toFixed(2)}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{
                      color: 'rgba(245,245,245,0.5)'
                    }} data-id="element-1732">
                          <span data-id="element-1733">or 4 payments of</span>
                          <span className="font-bold" style={{
                        color: '#F5F5F5'
                      }} data-id="element-1734">
                            {shopPayAmount}
                          </span>
                          <span data-id="element-1735">with</span>
                          <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-3.5 inline-block brightness-110" width="48" height="14" loading="lazy" decoding="async" data-id="element-1736" />
                        </div>
                      </div>
                    </label>;
              })}
              </div>
            </div>

            <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-1737">
              GET MY COLLECTOR'S EDITION →
            </OrangeButton>

            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-1738">
              <div className="flex items-center gap-1.5" data-id="element-1739">
                {[{
                v: hours,
                l: 'hrs'
              }, {
                v: minutes,
                l: 'min'
              }, {
                v: secs,
                l: 'sec'
              }].map(({
                v,
                l
              }) => <Fragment key={l} data-id="element-1740">
                    <div className="bg-[#1a1a1a] border border-white/10 text-white text-xs font-bold px-2 py-1 rounded min-w-[32px] text-center" data-id="element-1741">
                      {v}
                    </div>
                    <span className="text-[9px] text-white/50 uppercase font-bold tracking-wider" data-id="element-1742">
                      {l}
                    </span>
                  </Fragment>)}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-white/60 font-medium" data-id="element-1743">
                <ClockIcon className="h-3.5 w-3.5" style={{
                color: ACCENT
              }} data-id="element-1744" />{' '}
                lock in savings now
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 mt-5" data-id="element-1745">
              <div className="flex items-center gap-1.5" data-id="element-1746">
                <LockIcon className="h-4 w-4" style={{
                color: 'rgba(245,245,245,0.4)'
              }} data-id="element-1747" />
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{
                color: 'rgba(245,245,245,0.4)'
              }} data-id="element-1748">
                  Secure Checkout
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1749">
                <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-1750" />
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{
                color: 'rgba(245,245,245,0.4)'
              }} data-id="element-1751">
                  One-Time Payment
                </span>
              </div>
            </div>

            <button onClick={() => handleTierChange('BUNDLE')} className="mt-5 w-full py-2.5 rounded-lg text-sm font-bold tracking-wide transition-colors" style={{
            backgroundColor: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#F5F5F5'
          }} data-id="element-1752">
              ⊕ Bundle and save
            </button>
          </div>
        </div>
      </div>
    </section>;
};
// ─── C. IDENTIFICATION ───────────────────────────────────────────────────────
const IdentificationV6 = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1753">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-1754">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 font-future tracking-wide" data-id="element-1755">
        This Is For You If...
      </h2>
      <div className="space-y-4 mb-8" data-id="element-1756">
        {["You still get chills when the lightning hits the clock tower — and you can't fully explain why to anyone who wasn't there", "You've tried to show this film to your kids and realized you didn't have the words to explain why it's different from everything made since", "You've bought anniversary content before — and walked away feeling like the legacy deserved better", 'You feel like culture is moving too fast, and something that genuinely mattered from your generation is fading into noise'].map((t, i) => <div key={i} className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1757">
            <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1758">✅</span>
            <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1759">
              {t}
            </p>
          </div>)}
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(239,68,68,0.07)',
        border: '1px solid rgba(239,68,68,0.2)'
      }} data-id="element-1760">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1761">❌</span>
          <p className="text-white/70 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1762">
            This is NOT for you if you're looking for a plot recap, a "Did You
            Know?" trivia list, or anything you've already seen on a YouTube
            countdown
          </p>
        </div>
      </div>
      <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8 text-center italic" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1763">
        Most anniversary products treat you like a casual viewer. This one was
        built for the fan who already loves the film — and is ready to finally
        understand it.
      </p>
      <div className="text-center mb-8" data-id="element-1764">
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{
        color: ACCENT,
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1765">
          INTRODUCING
        </p>
        <h3 className="text-2xl md:text-3xl font-bold mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1766">
          BACK IN TIME
        </h3>
        <p className="text-white font-semibold text-lg md:text-xl mb-3" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1767">
          The 40th Anniversary Collector's Edition
        </p>
        <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1768">
          The most complete legacy document ever assembled for Back to the
          Future.
        </p>
      </div>
      <div className="flex justify-center" data-id="element-1769">
        <div className="relative w-full max-w-md" data-id="element-1770">
          <div className="absolute -inset-1 rounded-xl blur-md opacity-30" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-1771" />
          <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
          aspectRatio: '1/1'
        }} data-id="element-1772">
            <img src="https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png" alt="Back In Time" className="w-full h-full object-cover" loading="lazy" decoding="async" width="500" height="500" data-id="element-1773" />
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── D. WHAT YOU GET ─────────────────────────────────────────────────────────
const BookImage = ({
  src,
  alt,
  onClick
}: {
  src: string;
  alt: string;
  onClick: () => void;
}) => <div className="flex-shrink-0 w-full md:w-[160px]" data-id="element-1774">
    <div className="relative cursor-pointer group mx-auto" style={{
    maxWidth: '160px'
  }} onClick={onClick} data-id="element-1775">
      <img src={src} alt={alt} className="rounded-lg w-full md:w-[160px] object-cover shadow-lg transition-opacity group-hover:opacity-80" style={{
      aspectRatio: '3/4',
      maxWidth: '160px'
    }} loading="lazy" decoding="async" width="160" height="213" data-id="element-1776" />
      <p className="text-center text-[9px] mt-1 leading-tight" style={{
      color: ACCENT,
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1777">
        Tap to enlarge
      </p>
    </div>
  </div>;
const WhatYouGetV6 = () => {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const books = [{
    img: BOOK_IMAGES[0],
    alt: 'Back In Time',
    label: 'BOOK 1',
    title: 'IT DOCUMENTS THE LEGACY — NOT THE PLOT',
    desc: "190+ pages covering the cultural architecture of a story that's outlasted every trend, every franchise reboot, and every piece of technology it predicted. Why does it still feel alive? Why do complete strangers cry in the same place every time? This book answers all of it — with the depth the film has always deserved."
  }, {
    img: BOOK_IMAGES[1],
    alt: 'Behind The Clock Tower',
    label: 'BOOK 2',
    title: 'THE PRODUCTION TRUTH NOBODY PUBLISHED PROPERLY',
    desc: 'Michael J. Fox working 18-hour days — Family Ties from 10 AM to 6 PM, then the BTTF set until 2 AM. The DeLorean that kept breaking down on night shoots, requiring industrial hairdryers to unfreeze its gull-wing doors between takes. The casting decision that nearly bankrupted the production and cost $4 million to fix.',
    free: '$29.99'
  }, {
    img: BOOK_IMAGES[2],
    alt: 'Great Scott!',
    label: 'BOOK 3',
    title: '40 VOICES. 40 YEARS. EVERY ONE COUNTS.',
    desc: 'Not just the famous quotes. The layered ones. The ones that hit completely differently on the 20th viewing than they did on the first. Each one contextualized, with the real-world resonance documented behind it.',
    free: '$19.99'
  }, {
    img: BOOK_IMAGES[3],
    alt: 'Time. Imagination. Destiny.',
    label: 'BOOK 4',
    title: 'THE PHILOSOPHY THAT EXPLAINS WHY IT STILL HAUNTS YOU',
    desc: 'Why does a movie about a teenager in a time machine still feel this important 40 years later? This volume goes deeper than any film review ever has — exploring what Back to the Future says about free will, identity, ambition, and the courage required to change your future.',
    free: '$29.99'
  }];
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-1778">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-1779">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1780">
          What Makes This Different
        </h2>
        <p className="text-center text-white/50 text-sm mb-8" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1781">
          Four volumes. Every angle. Nothing left out.
        </p>
        <div className="space-y-6" data-id="element-1782">
          {books.map((b, i) => <div key={i} className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-1783">
              <BookImage src={b.img} alt={b.alt} onClick={() => setLightboxImage(b.img)} data-id="element-1784" />
              <div className="flex-1" data-id="element-1785">
                <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1786">
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1787">
                    {b.label}
                  </span>
                  {b.free && <span className="text-xs font-bold" style={{
                color: '#4ade80',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1788">
                      Retail Value:{' '}
                      <span className="line-through opacity-70" data-id="element-1789">{b.free}</span>{' '}
                      → FREE
                    </span>}
                </div>
                <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1790">
                  {b.title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1791">
                  {b.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>
      {lightboxImage && <ImageLightbox images={[lightboxImage]} startIndex={0} onClose={() => setLightboxImage(null)} data-id="element-1792" />}
    </section>;
};
// ─── E. PRICING + ORDER BUMPS ────────────────────────────────────────────────
const PurchaseV6 = () => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [bump2Expanded, setBump2Expanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [bump1LightboxOpen, setBump1LightboxOpen] = useState(false);
  const ctaRef = useRef<HTMLDivElement>(null);
  const bump2Price = bump1 ? BUMP2_PRICE_WITH_BUMP1 : BUMP2_PRICE_STANDALONE;
  const total = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? bump2Price : 0);
  const shopPayInstallment = (Math.ceil(total / 4 * 100) / 100).toFixed(2);
  const checkoutUrl = buildUrl(bump2 ? CHECKOUT_BUNDLE : bump1 ? CHECKOUT_PREMIUM : CHECKOUT_DIGITAL);
  const handleCta = () => {
    trackV6(bump2 ? "Time Traveler's Collector Bundle" : bump1 ? 'Premium Hardcover Bundle' : 'Complete Digital Collection', total);
  };
  const scrollToCta = () => {
    if (window.innerWidth < 768 && ctaRef.current) setTimeout(() => {
      ctaRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 50);
  };
  return <section id="pricing-section" style={{
    backgroundColor: BG,
    paddingTop: '32px',
    paddingBottom: '48px',
    scrollMarginTop: '80px'
  }} className="md:py-12" data-id="element-1793">
      <div className="container mx-auto px-4 md:px-10 max-w-2xl" data-id="element-1794">
        <div className="text-center mb-6 md:mb-10" data-id="element-1795">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3" style={{
          backgroundColor: 'rgba(255,108,0,0.15)',
          color: ACCENT,
          border: '1px solid rgba(255,108,0,0.3)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1796">
            🔥 40th Anniversary Launch Pricing
          </span>
          <h2 className="text-[22px] md:text-4xl font-bold text-center font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1797">
            Special 40th Anniversary Collector's Offer
          </h2>
          <p className="text-white/50 text-xs mt-2 max-w-sm mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1798">
            ⚠️ This price exists because it's the 40th anniversary. When the
            window closes, it closes.
          </p>
        </div>

        {/* MAIN PRODUCT CARD */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)]" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-1799">
          <div className="px-4 py-4 md:p-10" data-id="element-1800">
            <p className="text-[11px] md:text-2xl font-bold text-center uppercase tracking-widest mb-3 font-future" style={{
            color: ACCENT
          }} data-id="element-1801">
              The Complete Digital Collection
            </p>
            {/* Mobile: image + items side by side */}
            <div className="flex gap-3 items-center mb-3 md:hidden" data-id="element-1802">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Collection" className="rounded-lg shadow-md flex-shrink-0" style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover'
            }} loading="lazy" decoding="async" width="100" height="100" data-id="element-1803" />
              <div className="flex-1 grid grid-cols-1 gap-y-1" data-id="element-1804">
                {['Back In Time (190 pg)', 'Behind The Clock Tower (87 pg) — FREE', 'Great Scott! (87 pg) — FREE', 'Time. Imagination. Destiny. (71 pg) — FREE'].map(item => <div key={item} className="flex items-start gap-1.5" data-id="element-1805">
                    <span style={{
                  color: ACCENT,
                  fontSize: '8px',
                  flexShrink: 0,
                  marginTop: '4px'
                }} data-id="element-1806">
                      ✦
                    </span>
                    <span className="text-[11px] leading-tight" style={{
                  color: '#F5F5F5',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-1807">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            {/* Desktop: larger centered image like V4 */}
            <div className="hidden md:flex justify-center mb-6" data-id="element-1808">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" width="260" height="260" data-id="element-1809" />
            </div>
            <ul className="hidden md:block space-y-3 mb-6" data-id="element-1810">
              {['Back In Time (190 pages) — The Cultural Analysis', 'Behind the Clock Tower (87 pages) — The Production Story — FREE', 'Great Scott! (87 pages) — 40 Reflections for 40 Years — FREE', 'Time. Imagination. Destiny. (71 pages) — The Philosophical Legacy — FREE'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-1811">
                  <span className="font-bold mt-0.5" style={{
                color: ACCENT,
                flexShrink: 0
              }} data-id="element-1812">
                    ✦
                  </span>
                  <span className="text-base leading-relaxed" style={{
                color: '#F5F5F5',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1813">
                    {item}
                  </span>
                </li>)}
            </ul>
            <div className="text-center mb-2" data-id="element-1814">
              <span className="line-through text-sm md:text-lg opacity-40 mr-2" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1815">
                $139.94
              </span>
              <span className="font-bold text-[26px] md:text-3xl leading-none transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1816">
                ${total.toFixed(2)}
              </span>
              {(bump1 || bump2) && <span className="block text-[10px] text-white/40 mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1817">
                  w/ selected add-ons
                </span>}
            </div>
            <div className="text-center mb-2" data-id="element-1818">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2.5 py-1 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1819">
                You Save ${(139.94 - total).toFixed(2).replace('-', '')}
              </span>
            </div>
            <div className="mb-3 px-3 py-2 bg-black/30 rounded-lg border border-white/10 text-center max-w-xs mx-auto" data-id="element-1820">
              <div className="flex items-center justify-center gap-2 mb-0.5" data-id="element-1821">
                <span className="text-gray-300 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1822">
                  or 4 payments of
                </span>
                <span className="font-bold text-sm" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1823">
                  ${shopPayInstallment}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1.5" data-id="element-1824">
                <span className="text-gray-400 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1825">
                  with
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-1826" />
              </div>
            </div>
            <div ref={ctaRef} data-id="element-1827">
              <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-1828">
                GET MY COLLECTION →
              </OrangeButton>
            </div>
            <p className="text-center text-white/40 text-[10px] mt-1.5" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1829">
              Instant delivery. Readable on any device.
            </p>
            <div className="hidden md:flex items-center justify-center gap-5 mt-4" data-id="element-1830">
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1831">
                <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-1832" /> Secure
                Checkout
              </div>
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1833">
                <LockIcon className="h-4 w-4 text-orange-400" data-id="element-1834" /> One-Time
                Payment
              </div>
            </div>
          </div>
        </div>

        {/* BUMP 1 */}
        <div className="rounded-xl mb-3 overflow-hidden transition-all duration-300" style={{
        border: bump1 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump1 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump1 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-1835">
          <div className="px-4 py-2" style={{
          backgroundColor: bump1 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-1836">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1837">
              ⚡ Special One-Time Offer
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1838">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            setBump1(!bump1);
            if (!bump1) scrollToCta();
          }} data-id="element-1839">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump1 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1840">
                {bump1 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-1841" />}
              </div>
              <div data-id="element-1842">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1843">
                  Add the Premium Hardcover Edition{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-1844">
                    — Only +${BUMP1_PRICE} Today
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1845">
                  Own it. Display it. Keep it forever.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-3" data-id="element-1846">
              <div className="flex-shrink-0 cursor-pointer group" onClick={() => setBump1LightboxOpen(true)} data-id="element-1847">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-1848">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Premium Hardcover" className="rounded-lg shadow-lg w-full h-full object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" width="130" height="130" data-id="element-1849" />
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1850">
                  Tap to enlarge
                </p>
              </div>
              <ul className="space-y-1.5 flex-1" data-id="element-1851">
                {BUMP1_ITEMS.map(item => <BumpItem key={item} text={item} data-id="element-1852" />)}
              </ul>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump1 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            setBump1(!bump1);
            if (!bump1) scrollToCta();
          }} data-id="element-1853">
              {bump1 ? '✓ Added to my order' : `Add Premium Hardcover — Only +$${BUMP1_PRICE}`}
            </div>
          </div>
        </div>

        {/* BUMP 2 */}
        <div className="rounded-xl mb-5 overflow-hidden transition-all duration-300" style={{
        border: bump2 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump2 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump2 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-1854">
          <div className="px-4 py-2" style={{
          backgroundColor: bump2 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-1855">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump2 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1856">
              🚀 Ultimate Upgrade
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1857">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            setBump2(!bump2);
            if (!bump2) scrollToCta();
          }} data-id="element-1858">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump2 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1859">
                {bump2 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-1860" />}
              </div>
              <div data-id="element-1861">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1862">
                  Upgrade to the Complete Collector's Bundle{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-1863">
                    — Only +${bump2Price} Today
                    {bump1 && <span className="ml-2 text-xs font-normal text-white/35 line-through" data-id="element-1864">
                        ${BUMP2_PRICE_STANDALONE}
                      </span>}
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1865">
                  Every volume. Every format. The full archive.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-2" data-id="element-1866">
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setLightboxStart(0);
              setLightboxOpen(true);
            }} data-id="element-1867">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-1868">
                  <img src={BUNDLE_IMAGES[0]} alt="Collector Bundle" className="rounded-lg shadow-lg transition-opacity group-hover:opacity-80 w-full h-full object-cover" loading="lazy" decoding="async" width="130" height="130" data-id="element-1869" />
                  <div className="absolute bottom-1 right-1 bg-black/70 rounded px-1 py-0.5 flex gap-0.5" data-id="element-1870">
                    {[1, 2, 3].map(i => <div key={i} className="rounded-sm overflow-hidden" style={{
                    width: '12px',
                    height: '12px'
                  }} data-id="element-1871">
                        <img src={BUNDLE_IMAGES[i]} alt="" className="w-full h-full object-cover" decoding="async" loading="lazy" data-id="element-1872" />
                      </div>)}
                    <span className="text-white text-[8px] ml-0.5 leading-none self-center" data-id="element-1873">
                      +{BUNDLE_IMAGES.length - 4}
                    </span>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1874">
                  Tap to see all items
                </p>
              </div>
              <div className="flex-1" data-id="element-1875">
                <ul className="space-y-1.5" data-id="element-1876">
                  {BUMP2_ITEMS.slice(0, bump2Expanded ? BUMP2_ITEMS.length : 4).map(item => <BumpItem key={item} text={item} data-id="element-1877" />)}
                </ul>
                {BUMP2_ITEMS.length > 4 && <button type="button" className="flex items-center gap-1 text-[11px] mt-1.5" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }} onClick={e => {
                e.stopPropagation();
                setBump2Expanded(!bump2Expanded);
              }} data-id="element-1878">
                    <ChevronDownIcon className={`h-3 w-3 transition-transform ${bump2Expanded ? 'rotate-180' : ''}`} data-id="element-1879" />
                    {bump2Expanded ? 'Show less' : `+${BUMP2_ITEMS.length - 4} more items`}
                  </button>}
              </div>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none mt-2" style={{
            backgroundColor: bump2 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump2 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            setBump2(!bump2);
            if (!bump2) scrollToCta();
          }} data-id="element-1880">
              {bump2 ? '✓ Upgrade added' : `Upgrade to Collector Bundle — Only +$${bump2Price}`}
            </div>
          </div>
        </div>

        {/* FALLBACK */}
        <div className="rounded-xl py-4 px-5 text-center" style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1881">
          <p className="text-white/45 text-xs italic mb-3" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1882">
            Just want the core retrospective?
          </p>
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-1883">
            <img src="https://cdn.magicpatterns.com/uploads/ewSrEYsf3d6aAxsfRJbJ5B/BACK_IN_TIME_-_DIGITAL11v2.png" alt="Digital Edition" className="rounded-md" style={{
            width: '52px',
            height: '52px',
            objectFit: 'cover',
            opacity: 0.6
          }} loading="lazy" decoding="async" width="52" height="52" data-id="element-1884" />
            <div className="text-left" data-id="element-1885">
              <p className="text-white/55 text-sm font-medium" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1886">
                Digital Edition
              </p>
              <p className="text-white/45 text-sm" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1887">
                $39.97
              </p>
            </div>
          </div>
          <a href={buildUrl(CHECKOUT_BASIC)} onClick={() => trackV6('Digital Edition', 39.97)} className="text-white/40 text-xs underline hover:text-white/55 transition-colors" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1888">
            Get the Digital Edition
          </a>
        </div>
      </div>
      {lightboxOpen && <ImageLightbox images={BUNDLE_IMAGES} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} data-id="element-1889" />}
      {bump1LightboxOpen && <ImageLightbox images={['https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png']} startIndex={0} onClose={() => setBump1LightboxOpen(false)} data-id="element-1890" />}
    </section>;
};
// ─── F. TESTIMONIALS ─────────────────────────────────────────────────────────
const testimonials = [{
  quote: "I've collected BTTF merchandise for 20 years. Steelbooks, prints, the whole thing. This is the first time I've read something about this film that made me feel like the person who made it actually understood why it matters. Read the whole flagship book in one night.",
  name: 'Mark T., 47',
  loc: 'Columbus, Ohio'
}, {
  quote: "My son is 13. He'd seen the trilogy twice but didn't really get why I treat it like it's sacred. I read him the section on why fathers show this film to their kids. He went quiet for a second and then said 'that's literally us right now, Dad.' That's worth more than any price tag.",
  name: 'Sandra R., 52',
  loc: 'Austin, Texas'
}, {
  quote: "I went in skeptical, every 'definitive' BTTF product I've bought has been gorgeous packaging with nothing underneath. Behind The Clock Tower alone destroyed that pattern. The DeLorean hairdryer story, the Stoltz reshoot, the 18-hour Fox schedule, I've been a fan for 35 years and I didn't know any of it at this level of detail.",
  name: 'David M., 39',
  loc: 'San Diego, California'
}];
const TestimonialsV6 = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1891">
    <div className="container mx-auto px-4 max-w-4xl" data-id="element-1892">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 font-future tracking-wide text-white" data-id="element-1893">
        What Real Fans Are Saying
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1894">
        {testimonials.map((t, i) => <div key={i} className="p-5 rounded-xl flex flex-col" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1895">
            <div className="flex gap-0.5 mb-3" data-id="element-1896">
              {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1897" />)}
            </div>
            <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1898">
              "{t.quote}"
            </p>
            <div data-id="element-1899">
              <p className="text-white font-semibold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1900">
                {t.name}
              </p>
              <p className="text-white/40 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1901">
                {t.loc}
              </p>
            </div>
          </div>)}
      </div>
    </div>
  </section>;
// ─── G. VALUE SUMMARY ────────────────────────────────────────────────────────
const ValueSummaryV6 = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1902">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-1903">
      <div className="rounded-xl overflow-hidden" style={{
      border: `2px solid ${GOLD}`,
      backgroundColor: '#0d0d0d'
    }} data-id="element-1904">
        <div className="px-5 py-3 text-center" style={{
        backgroundColor: 'rgba(212,175,55,0.1)'
      }} data-id="element-1905">
          <span className="text-xs font-bold uppercase tracking-widest" style={{
          color: GOLD,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1906">
            Total Value Summary
          </span>
        </div>
        <div className="p-5 md:p-8" data-id="element-1907">
          <div className="space-y-3 mb-6" data-id="element-1908">
            {[{
            l: "Back In Time: 40th Anniversary Collector's Edition",
            v: '$59.97',
            gold: false
          }, {
            l: 'FREE: Behind The Clock Tower',
            v: '$29.99',
            gold: true
          }, {
            l: 'FREE: Great Scott! 40 Quotes For 40 Years',
            v: '$19.99',
            gold: true
          }, {
            l: 'FREE: Time, Imagination & Destiny',
            v: '$29.99',
            gold: true
          }].map((r, i) => <div key={i} className="flex justify-between items-center text-sm md:text-base" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1909">
                <span className="text-white/70" data-id="element-1910">{r.l}</span>
                <span className={r.gold ? 'font-semibold' : 'text-white font-semibold'} style={r.gold ? {
              color: GOLD
            } : {}} data-id="element-1911">
                  {r.v}
                </span>
              </div>)}
            <div className="border-t pt-3 mt-3 flex justify-between items-center" style={{
            borderColor: 'rgba(212,175,55,0.3)'
          }} data-id="element-1912">
              <span className="text-white font-bold text-sm md:text-base" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1913">
                TOTAL VALUE
              </span>
              <span className="text-white font-bold line-through opacity-50 text-sm md:text-base" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1914">
                $139.94
              </span>
            </div>
            <div className="flex justify-between items-center pt-2" data-id="element-1915">
              <span className="font-bold text-lg md:text-xl text-white" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1916">
                YOUR PRICE TODAY
              </span>
              <span className="font-bold text-3xl md:text-4xl" style={{
              color: ACCENT,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1917">
                $59.97
              </span>
            </div>
          </div>
          <OrangeButton href="#pricing-section" data-id="element-1918">
            GET MY COLLECTION — $59.97 →
          </OrangeButton>
        </div>
      </div>
    </div>
  </section>;
// ─── H. FAQ ──────────────────────────────────────────────────────────────────
const faqItems = [{
  q: 'What formats are the books available in?',
  a: 'The books are available in digital (PDF, instant download) and physical (premium hardcover or paperback, printed on demand). Some bundles include both formats. Digital editions are compatible with all devices, phones, tablets, computers, and e-readers.'
}, {
  q: 'How quickly will I receive my order?',
  a: "Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.\n\nPhysical books are professionally printed on demand and typically arrive within 9,14 days. The Time Traveler's Collector Bundle ships in 8,10 business days."
}, {
  q: 'Is this official/licensed content?',
  a: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment.'
}, {
  q: 'What makes this different from other BTTF books?',
  a: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision."
}, {
  q: 'Why only 1,985 copies?',
  a: "1985 is the year Back to the Future was released. The physical edition is limited to exactly 1,985 copies as a tribute to that moment, making each copy a genuine collector's item."
}, {
  q: 'Can I gift this to someone?',
  a: "Yes. Simply enter the recipient's shipping address at checkout for physical editions. For digital editions, the download link is sent to the email used at checkout."
}, {
  q: 'Who wrote this book?',
  a: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.'
}, {
  q: 'Is this book generated entirely by AI?',
  a: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.'
}, {
  q: 'Is there an audiobook version?',
  a: "Yes. The Back In Time Audiobook Experience is included exclusively in the Time Traveler's Collector Bundle."
}, {
  q: 'What if I have an issue with my order?',
  a: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help."
}];
const FAQV6 = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-1919">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-1920">
        <div className="text-center mb-8" data-id="element-1921">
          <h2 className="text-2xl md:text-3xl font-bold font-future text-white mb-2" data-id="element-1922">
            Questions? We've Got Answers.
          </h2>
          <div className="w-20 h-1 mx-auto rounded-full" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-1923" />
        </div>
        <div className="space-y-3" data-id="element-1924">
          {faqItems.map((item, index) => <div key={index} className="border rounded-lg overflow-hidden transition-colors duration-200" style={{
          borderColor: 'rgba(255,108,0,0.2)'
        }} data-id="element-1925">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 cursor-pointer" style={{
            backgroundColor: 'rgba(17,17,17,0.8)'
          }} aria-expanded={openIndex === index} data-id="element-1926">
                <span className="text-base font-semibold text-white pr-4" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1927">
                  {item.q}
                </span>
                <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} style={{
              color: ACCENT
            }} data-id="element-1928" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1929">
                <div className="px-5 pb-5 pt-1" style={{
              backgroundColor: 'rgba(11,11,11,0.6)'
            }} data-id="element-1930">
                  {item.a.split('\n\n').map((p, i) => <p key={i} className="text-sm leading-relaxed mb-3 last:mb-0" style={{
                color: 'rgba(245,245,245,0.6)',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1931">
                      {p}
                    </p>)}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
// ─── LANDING V6 EXPORT ────────────────────────────────────────────────────────
export const LandingV6 = () => <div className="w-full min-h-screen" style={{
  backgroundColor: BG,
  color: '#F5F5F5'
}} data-id="element-1932">
    <AlertBar data-id="element-1933" />
    <HeaderV6 data-id="element-1934" />
    <main data-id="element-1935">
      <HeroV6 data-id="element-1936" />
      <IdentificationV6 data-id="element-1937" />
      <WhatYouGetV6 data-id="element-1938" />
      <PurchaseV6 data-id="element-1939" />
      <TestimonialsV6 data-id="element-1940" />
      <ValueSummaryV6 data-id="element-1941" />
      <FAQV6 data-id="element-1942" />
    </main>
    <Footer data-id="element-1943" />
  </div>;